/**
 * The profile domain's VIEW MODELS and its one product vocabulary.
 *
 * ── Where these came from, and why the "Mock" prefix is gone ────────────────────────────────────
 *
 * All of this lived in `mocks/mock-talent-provider.tsx`, declared beside the fixture data that
 * used it — the same trap the recruiter app had: every screen needing a SHAPE imported a module
 * whose other half was invented people. The types were never mock; the profile page renders real
 * API rows through them. A type named `MockExperience` describing a real person's employment is a
 * name that lies about its contents, so the move is also the rename.
 *
 * `availabilityOptions` is real product vocabulary, not a fixture: 2/1/0 are the wire values
 * `job-preferences-client` maps to the server's status enum, and the labels are the product's own
 * wording. It lives here because availability is a fact about the PROFILE.
 */

export const availabilityOptions = [
  { id: 2, label: 'Actively looking', description: 'Show recruiters you are actively searching.' },
  { id: 1, label: 'Open to offers', description: 'You are open to hearing about relevant roles.' },
  { id: 0, label: 'Not open to offers', description: 'Pause new job and recruiter suggestions.' },
] as const

export type AvailabilityId = (typeof availabilityOptions)[number]['id']

export interface ExperienceView {
  id: string
  title: string
  company: string
  period: string
  location?: string
  description?: string
  companyLogoUrl?: string
}

export interface EducationView {
  id: string
  degree: string
  institution: string
  period: string
  institutionLogoUrl?: string
}

export interface ProfileSkillView {
  skillId: string | null
  skillName: string
}

export interface SocialLinksView {
  linkedin: string
  facebook: string
  twitter: string
  github: string
  website: string
}

export interface JobPreferencesView {
  workspaceTypes: string[]
  seniorityLevels: string[]
  jobTitles: string[]
  skills: JobPreferenceSkillView[]
  jobTypes: string[]
  desiredSalary: string
  currency: string
  desiredLocations: string[]
  companySizes: string[]
  industries: string[]
}

export interface JobPreferenceSkillView {
  skillId: string
  skillName: string
}

export type ProfileChecklistId =
  | 'registered'
  | 'resume'
  | 'education'
  | 'experience'
  | 'phone'
  | 'skills'
  | 'headline'
  | 'summary'

export interface ProfileChecklistItem {
  id: ProfileChecklistId
  checked: boolean
  text: string
  subText?: string
}

/** `{id, label}` for the preference pickers — ids are display strings or elId strings. */
export interface PreferenceOption {
  id: string
  label: string
}
